/** Per-discipline "how to play" copy, shown in the instructions overlay (Italian). */
export interface Instructions {
  /** One-line summary of the goal. */
  goal: string;
  /** Ordered how-to steps. */
  steps: string[];
  /** A short pro tip. */
  tip: string;
}

export const INSTRUCTIONS: Record<string, Instructions> = {
  sprint_100m: {
    goal: 'Corri i 100m più veloce di tutti.',
    steps: [
      'Tocca alternando SINISTRA e DESTRA (o i tasti A e D) a ritmo.',
      'Alternare pulito e costante è più veloce che pestare a caso.',
      'Trova la cadenza giusta: parte il 🔥 BOOST e voli.',
    ],
    tip: 'Negli ultimi metri continua a martellare per il tuffo sul traguardo.',
  },
  long_jump: {
    goal: 'Il salto più lungo, al meglio di 3 prove.',
    steps: [
      'Tocca SX/DX a ritmo per prendere velocità nella rincorsa.',
      'Premi SALTA appena prima della pedana bianca.',
      'Se superi la linea rossa il salto è NULLO.',
    ],
    tip: 'Più sei veloce allo stacco, più voli: ma non sforare!',
  },
  hurdles: {
    goal: '110m a ostacoli: corri e salta ogni barriera.',
    steps: [
      'Tocca SX/DX a ritmo per correre.',
      'Premi SALTA appena prima di ogni ostacolo.',
      'Se sbatti perdi tempo: tempismo > velocità cieca.',
    ],
    tip: 'Riprendi subito il ritmo dopo ogni salto per non perdere slancio.',
  },
  javelin: {
    goal: 'Lancia il giavellotto il più lontano possibile.',
    steps: [
      'Tocca SX/DX a ritmo per la rincorsa.',
      'Premi LANCIA prima della linea di fallo.',
      'Al lancio si blocca anche l’ANGOLO: punta a ~42°.',
    ],
    tip: 'Velocità alta + angolo nella zona verde = lancio massimo.',
  },
  high_jump: {
    goal: 'Supera l’asticella, che sale ogni volta.',
    steps: [
      'Tocca SX/DX a ritmo per la breve rincorsa.',
      'Premi STACCO vicino all’asticella (entro ~1m).',
      'Tre errori e la gara finisce: conta la misura migliore.',
    ],
    tip: 'Lo stacco al momento giusto vale più della pura velocità.',
  },
  triple_jump: {
    goal: 'Hop-Step-Jump: tre balzi in sequenza.',
    steps: [
      'Tocca SX/DX a ritmo per la rincorsa, poi premi SALTA sulla pedana.',
      'Seguono 3 stacchi: a ogni fase un cursore oscilla.',
      'Premi SALTA quando il cursore è al CENTRO (zona verde).',
    ],
    tip: 'Tre tempismi perfetti di fila = salto enorme.',
  },
  shot_put: {
    goal: 'Spingi il peso il più lontano possibile.',
    steps: [
      'Niente rincorsa: prima FERMA la barra POTENZA in alto.',
      'Poi blocca l’ANGOLO sulla zona verde (~40°).',
      'Il peso vola in un arco corto e pesante.',
    ],
    tip: 'Potenza al massimo + angolo perfetto fanno la differenza.',
  },
  hammer_throw: {
    goal: 'Fai girare il martello e lancialo lontano.',
    steps: [
      'Alterna SX/DX per aumentare la VELOCITÀ DI ROTAZIONE.',
      'Il martello orbita: quando brilla di verde è il momento giusto.',
      'Premi LANCIA in quell’istante per il rilascio perfetto.',
    ],
    tip: 'Più giri veloce e rilasci nella finestra verde, più lontano va.',
  },
  pole_vault: {
    goal: 'Scavalca l’asticella con l’asta: sale ogni volta.',
    steps: [
      'Tocca SX/DX a ritmo per la rincorsa con l’asta.',
      'Premi PIANTA vicino alla buca di battuta.',
      'Tre errori e finisci: conta l’altezza migliore.',
    ],
    tip: 'Tanta velocità + piantata al punto giusto = volo più alto.',
  },
  discus: {
    goal: 'Fai girare il disco e lancialo lontano.',
    steps: [
      'Alterna SX/DX per caricare i GIRI (più giri = più potenza).',
      'La barra RILASCIO oscilla: premi LANCIA quando è al centro (verde).',
      'Il disco vola in un lungo arco piatto.',
    ],
    tip: 'Tanti giri + rilascio nel verde = lancio massimo.',
  },
  weightlifting: {
    goal: 'Solleva più chili possibile e tienili su.',
    steps: [
      'Martella SX/DX per caricare la POTENZA, poi premi SOLLEVA.',
      'Più potenza = più chili tentati (ma più difficile da tenere).',
      'Tieni l’EQUILIBRIO: tocca il lato opposto a dove pende il bilanciere.',
    ],
    tip: 'Non esagerare col peso: se perdi l’equilibrio è NULLO.',
  },
  swim_50m: {
    goal: '50m stile libero: il tempo più basso vince.',
    steps: [
      'Ferma la barra TUFFO in alto per una partenza lanciata.',
      'Poi alterna SX/DX a ritmo per nuotare veloce.',
      'Gli avversari corrono sulla barra in alto: battili!',
    ],
    tip: 'Un buon tuffo + ritmo costante = record.',
  },
  diving: {
    goal: 'Tuffo acrobatico giudicato 0-100 punti.',
    steps: [
      'Ferma la SPINTA per saltare in alto dalla piattaforma.',
      'In aria alterna SX/DX per fare avvitamenti (giri).',
      'Premi ENTRA appena prima dell’acqua, da VERTICALE, per entrare pulito.',
    ],
    tip: 'Più giri + entrata dritta = punteggio alto. Spanciata = pochi punti!',
  },
  archery: {
    goal: '3 frecce: avvicinati al centro per più punti.',
    steps: [
      'Il mirino oscilla e il vento lo sposta.',
      'Premi SCOCCA quando il mirino è vicino al centro (oro = 10).',
      'Conta il totale delle 3 frecce (max 30).',
    ],
    tip: 'Anticipa l’oscillazione e compensa il vento.',
  },
};

export function getInstructions(disciplineId: string): Instructions | undefined {
  return INSTRUCTIONS[disciplineId];
}
